/**
 * EarlyAccessPopup — centered email-capture modal.
 *
 * Behaviour (per product spec):
 *  • Appears in the CENTER of the screen on PUBLIC pages.
 *  • Does NOT show on every page — it re-prompts only every few page views,
 *    so it keeps inviting browsers without nagging.
 *  • Once the visitor enters their email (here OR on the /early-access page),
 *    a persisted flag (`ea_joined`) stops it from ever asking that browser again.
 *  • Dismissing ("Not now" / backdrop / ✕) just defers it for a few more pages.
 *  • Includes a "Remove me" link to the self-service preferences page; removing
 *    there clears `ea_joined` so the person can be invited / rejoin later.
 *
 * Keys are intentionally NOT `jtr_`-prefixed so they survive the per-account
 * localStorage wipe on login (this is an anonymous-visitor flag).
 */
import { useEffect, useState, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import { Link, useLocation } from 'react-router-dom';
import { useLanguage } from '@/lib/i18n';
import { Sparkles, X, Mail, Check, Loader2, Crown, User, Building2, Shield, ChevronDown } from 'lucide-react';
import { useEarlyAccessCounts } from '@/lib/useEarlyAccessCounts';

const API = process.env.REACT_APP_BACKEND_URL;
export const EA_JOINED_KEY = 'ea_joined';
export const EA_OPTOUT_KEY = 'ea_popup_optout';
const NAV_KEY = 'ea_navc';
const NEXT_KEY = 'ea_next_prompt';
const REPROMPT_AFTER = 8;   // page views between prompts (toned down — don't nag)
const SHOW_DELAY_MS = 6000; // grace period before showing on a qualifying page

const EXCLUDED = ['/dashboard', '/admin', '/login', '/register', '/verify-email',
  '/early-access', '/email', '/forgot-password', '/account', '/federation', '/design-preview'];

const TRACKS = [
  { key: 'personal', Icon: User, ja: '個人', en: 'Personal', accent: 'text-emerald-700', ring: 'ring-emerald-500' },
  { key: 'business', Icon: Building2, ja: '法人', en: 'Business', accent: 'text-fuchsia-700', ring: 'ring-fuchsia-500' },
  { key: 'government', Icon: Shield, ja: '官公庁・公共部門', en: 'Government', accent: 'text-indigo-700', ring: 'ring-indigo-500' },
];

const ls = {
  get: (k, d) => { try { return localStorage.getItem(k) ?? d; } catch { return d; } },
  set: (k, v) => { try { localStorage.setItem(k, String(v)); } catch { /* noop */ } },
};

export default function EarlyAccessPopup() {
  const { lang } = useLanguage();
  const ja = lang === 'ja';
  const location = useLocation();
  const counts = useEarlyAccessCounts();
  const [show, setShow] = useState(false);
  const [track, setTrack] = useState('personal');
  const [email, setEmail] = useState('');
  const [more, setMore] = useState({ name: '', organization_name: '', role_title: '', country_region: '', estimated_etc_card_count: '', signup_note: '' });
  const [showMore, setShowMore] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [done, setDone] = useState(false);

  const fmt = (n) => Number(n || 0).toLocaleString();
  const isBusinessLike = track === 'business' || track === 'government';

  const excluded = EXCLUDED.some((p) => location.pathname.includes(p));
  const joined = useCallback(() => ls.get(EA_JOINED_KEY, '') === '1', []);
  const optedOut = useCallback(() => ls.get(EA_OPTOUT_KEY, '') === '1', []);
  const countedRef = useRef(null);
  const [eligible, setEligible] = useState(false);

  // Count each navigation exactly once (ref guard makes this idempotent under
  // React StrictMode's double-invoke) and decide whether this page is a
  // prompt point. Shows on the first eligible page, then every REPROMPT_AFTER
  // page views — never on every page, and never once joined.
  useEffect(() => {
    if (excluded || joined() || optedOut()) { setEligible(false); setShow(false); return; }
    if (countedRef.current === location.pathname) return;
    countedRef.current = location.pathname;
    const navc = Number(ls.get(NAV_KEY, '0')) + 1;
    ls.set(NAV_KEY, navc);
    // First prompt waits until the visitor has browsed REPROMPT_AFTER pages
    // (no longer shown on the very first page), then every REPROMPT_AFTER views.
    const nextAt = Number(ls.get(NEXT_KEY, String(REPROMPT_AFTER)));
    if (navc >= nextAt) {
      ls.set(NEXT_KEY, navc + REPROMPT_AFTER); // consume + set cooldown
      setEligible(true);
    } else {
      setEligible(false);
      setShow(false);
    }
  }, [excluded, joined, location.pathname]);

  // Reveal after a short grace period (separate effect so StrictMode can't
  // cancel the only timer mid-cycle). Before showing, do a final privacy-safe
  // server check: if this network/device is already on the waitlist (joined on
  // another browser), remember it locally and never prompt.
  useEffect(() => {
    if (!eligible) return undefined;
    let cancelled = false;
    const t = setTimeout(async () => {
      try {
        const r = await fetch(`${API}/api/early-access/recognize`);
        if (r.ok) {
          const d = await r.json();
          if (d.recognized) { ls.set(EA_JOINED_KEY, '1'); return; }
        }
      } catch { /* best-effort — fall through to show */ }
      if (!cancelled) setShow(true);
    }, SHOW_DELAY_MS);
    return () => { cancelled = true; clearTimeout(t); };
  }, [eligible]);

  const defer = () => setShow(false); // cooldown already set when shown
  const stopAsking = () => { ls.set(EA_OPTOUT_KEY, '1'); setShow(false); };

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    if (!email.includes('@')) { setError(ja ? '有効なメールアドレスを入力してください。' : 'Enter a valid email.'); return; }
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/early-access/join`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: email.trim(), account_type: track, preferred_language: lang,
          name: more.name, organization_name: more.organization_name,
          role_title: more.role_title, country_region: more.country_region,
          signup_note: more.signup_note,
          estimated_etc_card_count: more.estimated_etc_card_count ? Number(more.estimated_etc_card_count) : null,
          consent_email_updates: true, privacy_acknowledged: true,
          source_url: window.location.href, source_page_type: 'popup',
        }),
      });
      const data = await res.json();
      if (!res.ok) { setError(data.detail || (ja ? '登録に失敗しました。' : 'Something went wrong.')); return; }
      ls.set(EA_JOINED_KEY, '1'); // never ask this browser again
      setDone(true);
      setTimeout(() => setShow(false), 4500);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  if (!show) return null;

  const joinedCount = counts.claimed || 0;
  const remainingCount = counts.remaining != null ? counts.remaining : 100000;
  const limitCount = counts.limit || 100000;

  return createPortal(
    <div
      className="fixed inset-0 z-[9998] flex items-center justify-center p-4 bg-slate-950/70 backdrop-blur-sm animate-in fade-in duration-300"
      onClick={defer}
      data-testid="early-access-popup"
    >
      <div
        className="relative w-full max-w-md animate-in zoom-in-95 slide-in-from-bottom-2 duration-300"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Gradient glow so the white card pops on any page */}
        <div className="absolute -inset-1 rounded-[26px] bg-gradient-to-br from-emerald-400 via-purple-500 to-fuchsia-500 opacity-70 blur-lg" aria-hidden="true" />
        <div className="relative rounded-3xl bg-white text-slate-900 shadow-2xl ring-1 ring-black/5 overflow-hidden">
          {/* Header strip */}
          <div className="relative bg-gradient-to-br from-purple-700 to-fuchsia-700 text-white px-6 py-5">
            <button onClick={defer} data-testid="ea-popup-close"
              className="absolute top-3.5 right-3.5 text-white/70 hover:text-white transition-colors" aria-label="Close">
              <X className="w-5 h-5" />
            </button>
            <div className="flex items-center gap-2.5">
              <span className="inline-flex items-center justify-center w-10 h-10 rounded-xl bg-white/15 backdrop-blur">
                <Crown className="w-5 h-5 text-amber-200" />
              </span>
              <div>
                <p className="text-[10px] font-black uppercase tracking-[0.18em] text-purple-200">
                  {ja ? '先行受付リスト' : 'Early Access list'}
                </p>
                <h2 className="text-lg font-extrabold leading-tight">
                  {ja ? 'ローンチ時に優先案内' : 'Get first access at launch'}
                </h2>
              </div>
            </div>
          </div>

          <div className="px-6 pt-5 pb-6">
            {done ? (
              <div className="text-center py-6" data-testid="ea-popup-success">
                <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-gradient-to-br from-emerald-400 to-teal-500 mb-4">
                  <Check className="w-8 h-8 text-white" />
                </div>
                <p className="text-xl font-extrabold text-slate-900">{ja ? 'メールをご確認ください' : 'Check your email'}</p>
                <p className="mt-2 text-sm text-slate-500">{ja ? '確認リンクを送信しました。' : 'We sent you a confirmation link.'}</p>
                <div className="mt-4 rounded-xl border border-amber-200 bg-amber-50 px-3 py-2.5 text-left" data-testid="ea-popup-policy">
                  <p className="text-xs text-amber-800 leading-relaxed">
                    {ja
                      ? '⏳ 14日以内にリンクをクリックして確認してください。未確認の場合や、メールアドレスが無効でメールが届かない場合は、自動的にリストから削除されます（再登録が必要です）。'
                      : '⏳ Click the link within 14 days to confirm. Unconfirmed or invalid (bounced) emails are automatically removed from the list — you’d need to sign up again.'}
                  </p>
                </div>
              </div>
            ) : (
              <>
                {/* Live counter strip */}
                <div className="flex flex-wrap items-center gap-x-4 gap-y-1 rounded-xl bg-emerald-50 border border-emerald-200 px-3 py-2 mb-3" data-testid="ea-popup-counter">
                  <div className="flex items-center gap-1.5 text-[10px] font-black uppercase tracking-[0.14em] text-emerald-700">
                    <span className="relative flex h-1.5 w-1.5">
                      <span className="absolute inline-flex h-full w-full rounded-full bg-emerald-500 opacity-70 animate-ping" />
                      <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-emerald-500" />
                    </span>
                    {ja ? 'リスト枠' : 'List spots'}
                  </div>
                  <div className="flex items-baseline gap-1">
                    <span className="text-sm font-black tabular-nums text-slate-900">{fmt(joinedCount)}</span>
                    <span className="text-[11px] text-slate-500">{ja ? '登録済み' : 'joined'}</span>
                  </div>
                  <div className="flex items-baseline gap-1">
                    <span className="text-sm font-black tabular-nums text-emerald-700">{fmt(remainingCount)}</span>
                    <span className="text-[11px] text-slate-500">/ {fmt(limitCount)} {ja ? '残り' : 'left'}</span>
                  </div>
                </div>

                <p className="text-[13px] text-slate-600 leading-relaxed mb-3">
                  {ja
                    ? '先着100,000名様は、アカウント有効化開始時に1年間Premium Trialアカウントを優先取得できます。'
                    : 'The first 100,000 on the list get priority access to claim a 1-Year Premium Trial when activation opens.'}
                </p>

                {/* Track segmented control (compact pills) */}
                <label className="block text-[11px] font-bold uppercase tracking-wide text-slate-500 mb-1.5">
                  {ja ? 'ご関心のあるタイプ' : "I'm interested in"}
                </label>
                <div role="radiogroup" className="grid grid-cols-3 gap-1.5 p-1 rounded-xl bg-slate-100 mb-3">
                  {TRACKS.map((t) => {
                    const selected = track === t.key;
                    return (
                      <button key={t.key} type="button" role="radio" aria-checked={selected}
                        onClick={() => setTrack(t.key)}
                        data-testid={`ea-popup-track-${t.key}`}
                        className={`flex items-center justify-center gap-1.5 rounded-lg px-2 py-2 text-[13px] font-bold transition-all ${
                          selected ? `bg-white shadow-sm ring-2 ${t.ring} ${t.accent}` : 'text-slate-500 hover:text-slate-700'}`}>
                        <t.Icon className="w-3.5 h-3.5" strokeWidth={2.4} />
                        {ja ? t.ja : t.en}
                      </button>
                    );
                  })}
                </div>

                <form onSubmit={submit}>
                  <div className="flex flex-col sm:flex-row gap-2">
                    <div className="relative flex-1">
                      <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                      <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
                        placeholder="you@example.com" autoComplete="email" data-testid="ea-popup-email"
                        className="w-full rounded-xl bg-slate-50 border border-slate-300 pl-10 pr-3 py-3 text-slate-900 placeholder-slate-400 focus:bg-white focus:border-purple-500 focus:outline-none focus:ring-2 focus:ring-purple-500/30" />
                    </div>
                    <button type="submit" disabled={loading} data-testid="ea-popup-submit"
                      className="shrink-0 inline-flex items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-purple-600 to-fuchsia-600 px-5 py-3 text-sm font-extrabold text-white hover:from-purple-500 hover:to-fuchsia-500 disabled:opacity-60 transition-all">
                      {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
                      {ja ? '登録' : 'Join'}
                    </button>
                  </div>
                  {error && <p className="mt-2.5 text-sm text-rose-600" data-testid="ea-popup-error">{error}</p>}

                  {/* Optional details — collapsed by default. All we need is the email. */}
                  <button type="button" onClick={() => setShowMore((v) => !v)}
                    data-testid="ea-popup-more-toggle"
                    className="mt-2.5 inline-flex items-center gap-1 text-[12px] font-semibold text-purple-700 hover:text-purple-900">
                    <ChevronDown className={`w-3.5 h-3.5 transition-transform ${showMore ? 'rotate-180' : ''}`} />
                    {ja ? '詳細を追加（任意・不要）' : 'Add more details (optional)'}
                  </button>
                  {showMore && (
                    <div className="mt-2.5" data-testid="ea-popup-more-fields">
                      <p className="text-[11px] text-slate-500 leading-snug mb-2">
                        {ja
                          ? '入力は任意です。必要なのはメールアドレスだけですが、ご希望であれば組織や事業の情報を追加できます。'
                          : 'These are optional — all we need is your email. If you’d like, you can add details about your organization or business.'}
                      </p>
                      <div className="grid grid-cols-2 gap-2">
                        <input type="text" value={more.name} onChange={(e) => setMore({ ...more, name: e.target.value })}
                          placeholder={ja ? 'お名前' : 'Full name'} data-testid="ea-popup-name"
                          className="rounded-lg bg-slate-50 border border-slate-300 px-3 py-2 text-sm text-slate-900 placeholder-slate-400 focus:bg-white focus:border-purple-500 focus:outline-none" />
                        <input type="text" value={more.country_region} onChange={(e) => setMore({ ...more, country_region: e.target.value })}
                          placeholder={ja ? '国・地域' : 'Country / region'} data-testid="ea-popup-region"
                          className="rounded-lg bg-slate-50 border border-slate-300 px-3 py-2 text-sm text-slate-900 placeholder-slate-400 focus:bg-white focus:border-purple-500 focus:outline-none" />
                        <input type="number" min="0" value={more.estimated_etc_card_count} onChange={(e) => setMore({ ...more, estimated_etc_card_count: e.target.value })}
                          placeholder={ja ? 'ETCカード枚数の目安' : 'Estimated ETC cards'} data-testid="ea-popup-cards"
                          className="col-span-2 rounded-lg bg-slate-50 border border-slate-300 px-3 py-2 text-sm text-slate-900 placeholder-slate-400 focus:bg-white focus:border-purple-500 focus:outline-none" />
                        {isBusinessLike && (
                          <>
                            <input type="text" value={more.organization_name} onChange={(e) => setMore({ ...more, organization_name: e.target.value })}
                              placeholder={ja ? '組織名' : 'Organization'} data-testid="ea-popup-org"
                              className="rounded-lg bg-slate-50 border border-slate-300 px-3 py-2 text-sm text-slate-900 placeholder-slate-400 focus:bg-white focus:border-purple-500 focus:outline-none" />
                            <input type="text" value={more.role_title} onChange={(e) => setMore({ ...more, role_title: e.target.value })}
                              placeholder={ja ? '役職' : 'Role / title'} data-testid="ea-popup-role"
                              className="rounded-lg bg-slate-50 border border-slate-300 px-3 py-2 text-sm text-slate-900 placeholder-slate-400 focus:bg-white focus:border-purple-500 focus:outline-none" />
                          </>
                        )}
                        <textarea rows={2} value={more.signup_note} onChange={(e) => setMore({ ...more, signup_note: e.target.value })}
                          placeholder={ja ? 'ご要望・メモ' : 'Notes'} data-testid="ea-popup-note"
                          className="col-span-2 rounded-lg bg-slate-50 border border-slate-300 px-3 py-2 text-sm text-slate-900 placeholder-slate-400 focus:bg-white focus:border-purple-500 focus:outline-none resize-none" />
                      </div>
                    </div>
                  )}
                  <p className="mt-2.5 text-[11px] text-slate-400 leading-snug">
                    {ja
                      ? '登録により更新メール受信に同意。本サービスはNEXCO・MEISAIとは無関係の独立サービスです。いつでも配信停止可。'
                      : 'By joining you agree to receive updates. JTR is independent — not NEXCO or MEISAI. Unsubscribe anytime.'}
                  </p>
                </form>

                <div className="mt-3 flex items-center justify-between text-xs text-slate-400">
                  <button onClick={defer} className="hover:text-slate-600" data-testid="ea-popup-dismiss">
                    {ja ? '今はしない' : 'Not now'}
                  </button>
                  <Link to={ja ? '/ja/email/preferences' : '/en/email/preferences'} onClick={() => setShow(false)} className="hover:text-slate-600" data-testid="ea-popup-remove">
                    {ja ? '登録済み？解除する' : 'Already signed up? Remove me'}
                  </Link>
                </div>
                <div className="mt-3 pt-3 border-t border-slate-100 text-center">
                  <button onClick={stopAsking} data-testid="ea-popup-stop-asking"
                    className="text-[11px] text-slate-400 hover:text-slate-600 transition-colors underline underline-offset-2">
                    {ja ? '今後このポップアップを表示しない' : 'Don\u2019t show this popup again'}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}
